/**
 * Глобальный интерцептор fetch с автоматическим обновлением токенов при 401
 *
 * Единственный механизм для:
 * 1. Добавления Authorization header к API запросам
 * 2. Обработки 401 — refresh токена и retry запроса
 *
 * ВАЖНО: Никакой другой код НЕ должен обрабатывать 401 или добавлять Authorization header.
 * authProvider, fetchMe, loadPermissions — все полагаются на этот interceptor.
 * Redirect на /login при неудачном refresh — ответственность Refine (authProvider.check).
 */

import { refreshTokens } from '@/providers/auth-provider';
import { getActiveTenant } from '@/lib/active-tenant';

// Сохраняем оригинальный fetch
const originalFetch = window.fetch.bind(window);

// Флаг для предотвращения параллельных refresh запросов
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Проверяет, нужно ли добавлять Authorization header к запросу
 */
function shouldAddAuthHeader(url: string): boolean {
  // Добавляем только для API запросов (наш бэкенд)
  const isApiRequest = url.includes('/api/') ||
                       url.startsWith('/api') ||
                       url.includes('localhost:3000') ||
                       url.includes('127.0.0.1:3000');

  // Не добавляем для auth endpoints (login, refresh)
  const isAuthEndpoint = url.includes('/auth/admin/login') ||
                         url.includes('/auth/admin/refresh');

  return isApiRequest && !isAuthEndpoint;
}

/**
 * Проставляет заголовок активного тенанта.
 * В однотенантном режиме (slug = null) заголовок НЕ добавляется —
 * поведение остаётся идентичным до-tenant версии.
 */
function applyTenantHeader(headers: Headers): void {
  const tenant = getActiveTenant();
  if (tenant && !headers.has('X-Tenant-Id')) {
    headers.set('X-Tenant-Id', tenant);
  }
}

/**
 * Добавляет Authorization header к запросу
 */
function addAuthHeader(init: RequestInit | undefined): RequestInit {
  const token = localStorage.getItem('accessToken');

  if (!token) {
    const headersNoToken = new Headers(init?.headers);
    applyTenantHeader(headersNoToken);
    return { ...init, headers: headersNoToken };
  }

  const headers = new Headers(init?.headers);

  // Не перезаписываем если уже есть
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  applyTenantHeader(headers);

  return {
    ...init,
    headers,
  };
}

/**
 * Обновляет Authorization header с новым токеном после refresh
 */
function updateAuthHeader(init: RequestInit | undefined): RequestInit {
  const token = localStorage.getItem('accessToken');

  if (!token) {
    return init || {};
  }

  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  applyTenantHeader(headers);

  return {
    ...init,
    headers,
  };
}

/**
 * Перехватчик fetch с автоматическим обновлением токенов
 *
 * При 401:
 * 1. Пытается refresh токен
 * 2. Если успешно — повторяет запрос с новым токеном
 * 3. Если refresh не удался — возвращает оригинальный 401 response
 *    (Refine через authProvider.check() обработает redirect)
 */
async function fetchInterceptor(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const url = typeof input === 'string' ? input : (input instanceof URL ? input.href : input.url);

  // Добавляем auth header если нужно
  const requestInit = shouldAddAuthHeader(url) ? addAuthHeader(init) : init;

  // Выполняем запрос
  const response = await originalFetch(input, requestInit);

  // Если не 401 или это auth endpoint — возвращаем как есть
  if (response.status !== 401 || !shouldAddAuthHeader(url)) {
    return response;
  }

  // Если уже идёт refresh — ждём его завершения
  if (isRefreshing && refreshPromise) {
    const refreshed = await refreshPromise;
    if (refreshed) {
      const retryInit = updateAuthHeader(init);
      return originalFetch(input, retryInit);
    }
    // Refresh не удался — возвращаем оригинальный 401
    return response;
  }

  // Начинаем refresh
  isRefreshing = true;
  refreshPromise = refreshTokens();

  try {
    const refreshed = await refreshPromise;

    if (!refreshed) {
      // Возвращаем оригинальный 401 — Refine обработает redirect через authProvider
      return response;
    }

    // Повторяем запрос с новым токеном
    const retryInit = updateAuthHeader(init);
    return originalFetch(input, retryInit);
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
}

/**
 * Устанавливает глобальный интерцептор fetch
 * Вызывать один раз при старте приложения
 */
export function setupFetchInterceptor(): void {
  window.fetch = fetchInterceptor;
}

/**
 * Восстанавливает оригинальный fetch (для тестов)
 */
export function restoreFetch(): void {
  window.fetch = originalFetch;
}

// Для обратной совместимости экспортируем хелперы
export { fetchInterceptor as fetchWithAuth };

export function getAuthHeaders(): HeadersInit {
  const accessToken = localStorage.getItem('accessToken');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return headers;
}
